import { BalanceTile, BalanceTileRow } from './BalanceTiles'
import { useSageAccess } from '@/hooks/useSageAccess'
import type { BankingKpi, BankingKpiWarnings } from '@/types/banking'

interface KpiCardsProps {
  /**
   * The aggregated balances. `warnings` is optional so the same row can be fed
   * either a `BankingKpiResult` (Dashboard/Import/Outstanding Checks, which
   * compute per-tile freshness warnings) or a plain `CrossCheckingKpi`.
   */
  kpi?: BankingKpi & { warnings?: BankingKpiWarnings }
  loading?: boolean
}

/**
 * KPI card data definition
 */
interface KpiCardData {
  label: string
  key: keyof BankingKpi
  colorClass: string
  tooltip?: string
  /** Sourced from Sage Intacct: hidden for organizations without the integration. */
  sage: boolean
}

// DCP-218 AC.4: every tile carries a tooltip explaining how its value is
// derived. The copy below is the canonical "HINT TEXTS" wording from the
// tickets (DCP-218, DCP-347 for the variance; verbatim) — keep it in sync with
// Linear, not paraphrased.
//
// DCP-371 AMENDED the Bank Balance string, deliberately diverging from the
// DCP-218 text in Linear: it used to say "the sum of ALL bank balances",
// which stopped being true once credit cards were excluded from the figure.
// A tooltip that overstates what a money tile counts is worse than no tooltip,
// so the wording follows the behaviour, not the older ticket. Do NOT "restore"
// it from DCP-218's Linear copy — that would reintroduce the false
// claim. The exclusion itself lives in BANK_BALANCE_EXCLUDED_ACCOUNT_TYPES
// (constants/banking.ts); if that set ever widens, widen this copy with it.
const kpiCards: KpiCardData[] = [
  { label: 'Bank Balance', key: 'bankBalance', colorClass: 'text-foreground', sage: false, tooltip: "The sum of your bank account balances as of today's date, based on the most recent data from the banks. Credit card accounts are not included." },
  { label: 'Outstanding Checks', key: 'outstandingChecks', colorClass: 'text-red-600', sage: false, tooltip: 'The total amount of all checks that have been issued but not yet cleared by the bank.' },
  { label: 'Book Balance', key: 'bookBalance', colorClass: 'text-green-600', sage: false, tooltip: 'Available cash position: Bank Balance minus Outstanding Checks.' },
  { label: 'Intacct Balance', key: 'intacctBalance', colorClass: 'text-green-600', sage: true, tooltip: 'The sum of all book balances from Sage Intacct for the current date, used to cross-check against your bank-sourced figures.' },
  // Neutral colour on purpose: a variance in either direction is a discrepancy
  // to look at, not a gain or a loss.
  { label: 'Book vs. Intacct Variance', key: 'bookVsIntacct', colorClass: 'text-foreground', sage: true, tooltip: 'The difference between Book Balance and Intacct Balance across all included accounts: Book Balance − Intacct Balance.' },
]

/**
 * Aggregated balances row shown on every Banking tab (DCP-297).
 * Displays Bank Balance, Outstanding Checks, Book Balance, Intacct Balance and
 * Book vs. Intacct Variance.
 *
 * The two Sage tiles are gated here, not by each page, so every tab applies the
 * same rule: hidden once the organization is known not to have Sage Intacct.
 * Until the organization is known the whole row is skeletons. A `null` value is a figure that could not be loaded and
 * renders as "—".
 *
 * This component owns only the metric definitions; the tile geometry that keeps
 * the values on one bottom edge lives in `BalanceTile`.
 */
export function KpiCards({ kpi, loading }: KpiCardsProps) {
  const { isEnabled: sageEnabled, isResolved: sageResolved } = useSageAccess()
  const cards = sageResolved && !sageEnabled ? kpiCards.filter((card) => !card.sage) : kpiCards
  // Without an organization the KPI query is disabled (not loading), so the
  // values would read $0.00 and the Sage tiles could still vanish: skeletons.
  const rowLoading = loading || !sageResolved

  return (
    <BalanceTileRow>
      {cards.map((card) => (
        <BalanceTile
          key={card.key}
          label={card.label}
          // No KPI outside loading means the query failed (pages do not read
          // isError): "—", never $0.00 — a zero variance would read as "books
          // reconcile". Not `?? 0` either: a failed Intacct read (null) stays null.
          value={kpi ? kpi[card.key] : null}
          valueClassName={card.colorClass}
          tooltip={card.tooltip}
          // DCP-218 AC.3: a per-tile reason string means current-day data is
          // missing/stale for this metric — surface a warning icon + tooltip.
          warning={kpi?.warnings?.[card.key]}
          loading={rowLoading}
        />
      ))}
    </BalanceTileRow>
  )
}
